// src/features/project/OverviewPanel.tsx
// Compact project snapshot from GetProjectSummary: progress, active sprint, next
// milestone, blockers, team workload and recent activity.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProject } from '@react/hooks';
import { Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { ProgressBar } from './ProjectModal';
import { getDisplayName, formatDate, formatRelativeTime, sprintStatusMap, getStatusMapping } from './logic';
import { milestoneStatusMap } from './meta';
import type { Entity } from '@core/types/entity';
import type { GetProjectSummaryResponse } from '@core/types/project';

export const OverviewPanel = observer(({ projectEntity }: { projectEntity: Entity }) => {
  const { t, locale } = useI18n();
  const store = useProject();
  const [summary, setSummary] = useState<GetProjectSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store
      .GetProjectSummary(projectEntity.id)
      .then((res) => alive && setSummary(res))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectEntity.id]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-40 animate-pulse rounded-lg bg-muted/50" />
        ))}
      </div>
    );
  }

  if (store.error && !summary) {
    return <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />;
  }

  if (!summary) return null;

  const stats = summary.stats;
  const progress = stats?.total_items ? Math.round((stats.completed_items / stats.total_items) * 100) : 0;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Progress */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('project.overview.progress')}</h3>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1">
              <ProgressBar percent={progress} />
            </div>
            <span className="text-sm font-medium text-foreground">{progress}%</span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <MiniStat label={t('project.analytics.completedItems')} value={stats?.completed_items ?? 0} />
            <MiniStat label={t('project.analytics.inProgress')} value={stats?.in_progress_items ?? 0} />
            <MiniStat label={t('project.analytics.blocked')} value={stats?.blocked_items ?? 0} />
          </div>
        </CardContent>
      </Card>

      {/* Active sprint + next milestone */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('project.overview.now')}</h3>
          <div className="mt-3 space-y-3">
            {summary.active_sprint ? (
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{summary.active_sprint.name}</span>
                  <Badge variant={getStatusMapping(sprintStatusMap, summary.active_sprint.status).variant as never} size="sm">
                    {t(getStatusMapping(sprintStatusMap, summary.active_sprint.status).labelKey)}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {summary.active_sprint.completed_items ?? 0}/{summary.active_sprint.total_items ?? 0}{' '}
                  {t('project.sprints.items')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('project.overview.noActiveSprint')}</p>
            )}
            {summary.next_milestone && (
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{summary.next_milestone.name}</span>
                  <Badge variant={getStatusMapping(milestoneStatusMap, summary.next_milestone.status).variant as never} size="sm">
                    {t(getStatusMapping(milestoneStatusMap, summary.next_milestone.status).labelKey)}
                  </Badge>
                </div>
                {summary.next_milestone.due_date && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatDate(summary.next_milestone.due_date, locale)}
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Blockers */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('project.overview.blockers')}</h3>
          {(summary.blockers?.length ?? 0) === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">{t('project.overview.noBlockers')}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {summary.blockers.map((b, i) => (
                <li key={i} className="rounded-lg border border-border p-2.5">
                  <p className="text-sm text-foreground">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.blocked_by_reason}</p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Workload */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('project.overview.workload')}</h3>
          {(summary.workload?.length ?? 0) === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">{t('project.overview.noWorkload')}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {summary.workload.map((w, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{getDisplayName(w.assignee)}</span>
                  <span className="text-muted-foreground">
                    {w.in_progress_items ?? 0} {t('project.overview.active')} · {w.total_items ?? 0}{' '}
                    {t('project.overview.total')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card className="lg:col-span-2">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground">{t('project.overview.recentActivity')}</h3>
          {(summary.recent_activity?.length ?? 0) === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">{t('project.overview.noActivity')}</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {summary.recent_activity.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-foreground">
                    <span className="text-muted-foreground">{getDisplayName(a.actor)}</span> {a.description}
                  </span>
                  <span className="flex-shrink-0 text-xs text-muted-foreground">
                    {formatRelativeTime(a.timestamp, locale)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

OverviewPanel.displayName = 'OverviewPanel';

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-xl font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
